import { createExpression } from '@shared/helpers/expression-factory.helpers';
import {
  ElementCategoryID,
  IElementCategory,
} from '../../../models/element-categories.model';
import {
  ElementPropertyType,
  IFactoryProperty,
} from '../../../models/element-property.model';
import { ArrayItem } from '../../../models/type-helpers.model';
import {
  CoolingPropertyName,
  CoolingType,
  DEFAULT_MAX_CAPACITY_PER_M2,
} from './cooling.model';
import { EXPRESSION_VARIABLE_NAME } from '@shared/constants/expression-variables-names.constant';
import { commonElementCategoryProperties } from '../common-element-category-properties.model';

const nominalEfficiencyProperty: IFactoryProperty = {
  name: CoolingPropertyName.NominalEfficiency,
  displayName: 'Nominal Efficiency (EER)',
  type: ElementPropertyType.Expression,
  unit: 'none',
  indentation: 1,
  min: 1,
  max: 10,
  fallbackCount: createExpression(
    `${CoolingPropertyName.Type} == "${CoolingType.Chiller}" ? 2.9 : 3.2`,
    2.9,
  ),
  description:
    'Nominal efficiency for cooling mode. Typical 2–5. 5 means that for every 1 kW of electricity, the chiller can remove 5 kW of heat from the building.',
};

const chillerProperties: ArrayItem<IElementCategory['properties']> = {
  conditions: {
    [CoolingPropertyName.Type]: CoolingType.Chiller,
  },
  nodes: [
    nominalEfficiencyProperty,
    {
      name: CoolingPropertyName.SeasonalEfficiency,
      displayName: 'Seasonal Efficiency (ESEER/IPLV)',
      type: ElementPropertyType.Expression,
      unit: 'none',
      indentation: 1,
      min: 1,
      max: 15,
      fallbackCount: '4.35',
    },
    // Scoped out to DEV-2429
    // {
    //   name: CoolingPropertyName.COP,
    //   displayName: 'Heating Performance (COP)',
    //   type: ElementPropertyType.Expression,
    //   unit: 'none',
    //   indentation: 1,
    //   min: 0,
    //   max: 10,
    //   fallbackCount: '0',
    // },
  ],
};

const dxProperties: ArrayItem<IElementCategory['properties']> = {
  conditions: {
    [CoolingPropertyName.Type]: CoolingType.DirectExpansion,
  },
  nodes: [
    nominalEfficiencyProperty,
    {
      name: CoolingPropertyName.SeasonalEfficiency,
      displayName: 'Seasonal Efficiency (ESEER/IPLV)',
      type: ElementPropertyType.Expression,
      unit: 'none',
      indentation: 1,
      min: 0,
      max: 15,
      fallbackCount: `${CoolingPropertyName.NominalEfficiency} * 1.5`,
    },
    // TODO: ENABLE LATER
    // {
    //   name: CoolingPropertyName.COP,
    //   displayName: 'Heating Performance (COP)',
    //   type: ElementPropertyType.Expression,
    //   unit: 'none',
    //   indentation: 1,
    //   min: 0,
    //   max: 10,
    //   fallbackCount: '2.5',
    // },
  ],
};

/**
 * Properties specific to district cooling
 */
const districtCoolingProperties: ArrayItem<IElementCategory['properties']> = {
  conditions: {
    [CoolingPropertyName.Type]: CoolingType.DistrictCooling,
  },
  nodes: [
    {
      name: CoolingPropertyName.Efficiency,
      displayName: 'Efficiency',
      type: ElementPropertyType.Expression,
      unit: '%',
      indentation: 1,
      min: 0,
      max: 1,
      fallbackCount: '92%',
      description:
        'Share of delivered cooling that can be utilized by the building.',
    },
  ],
};

export const coolingProperties: IElementCategory['properties'] = [
  {
    name: CoolingPropertyName.MaxCapacity,
    type: ElementPropertyType.Expression,
    unit: 'kW',
    min: 0,
    fallbackCount: `${DEFAULT_MAX_CAPACITY_PER_M2} * ${EXPRESSION_VARIABLE_NAME.HEATED_AREA} / (${EXPRESSION_VARIABLE_NAME.ACTIVE_ELEMENTS_COUNT_BY_CATEGORY}["${ElementCategoryID.Cooling}"] or 1)`,
    description:
      'Installed max cooling capacity (kW). Optional; used for diagnostics.',
  },
  {
    name: CoolingPropertyName.Type,
    type: ElementPropertyType.Select,
    options: Object.values(CoolingType),
    count: CoolingType.Chiller,
  },
  chillerProperties,
  dxProperties,
  districtCoolingProperties,
  ...commonElementCategoryProperties,
];
